import { EntityType } from '@frontend/common';
import { DefaultBadge } from '@frontend/components/badge';
import { defaultCellClassName } from '@frontend/components/list';
import { ReactiveObjectList } from '@frontend/components/list-v2';
import { Functionality } from '@frontend/functionality/types';
import { CommonMessage } from '@frontend/lang';
import { RoleExtended } from '@frontend/role/types';
import { FormattedMessage } from 'react-intl';

import { DefaultContainer } from '../../../modal/detail-modal/default-container/default-container';
import { PlatformObjectBadge } from '../../../modal/platform-object-badge/platform-object-badge.component';
import { useRolesAndLicenses } from './roles-and-licenses.controller';

export interface RolesAndLicensesProps {
    entity_id: string;
    entity_type?: EntityType;
}

export const RolesAndLicenses = (props: RolesAndLicensesProps) => {
    const viewProps = useRolesAndLicenses(props);

    return (
        <>
            <ReactiveObjectList<RoleExtended>
                label={CommonMessage.OBJECTS.ROLE.PLURAL}
                fetch={() => Promise.resolve({ count: viewProps.roleItems.length, results: viewProps.roleItems })}
                columns={{
                    columns: ['role', 'type'],
                    customCells: new Map([
                        [
                            'role',
                            (obj) => (
                                <td className={defaultCellClassName}>
                                    <PlatformObjectBadge
                                        entityType={EntityType.ROLE}
                                        id={obj.row.original.role_id ?? ''}
                                        display={obj.row.original.name ?? ''}
                                        disableClick
                                        className='text-foreground-default inline-flex items-center justify-start gap-1 rounded-sm px-1 text-sm font-semibold'
                                    />
                                </td>
                            )
                        ],
                        [
                            'type',
                            (obj) => (
                                <td className={defaultCellClassName}>
                                    <DefaultBadge
                                        styles={{ container: { color: obj.row.original.group ? 'bg-brand-muted' : 'bg-brand-primary' } }}
                                        label={
                                            obj.row.original.group || viewProps.entityType === EntityType.USER_GROUP ? (
                                                <FormattedMessage
                                                    id='role.user-group-role'
                                                    defaultMessage='User group role'
                                                />
                                            ) : (
                                                <FormattedMessage
                                                    id='role.user-role'
                                                    defaultMessage='User role'
                                                />
                                            )
                                        }
                                    />
                                </td>
                            )
                        ]
                    ])
                }}
                dispatch={viewProps.dispatch}
                type={EntityType.ROLE}
                useStaticListId
                id={viewProps.ROLE_LIST_ID}
                tableProps={{ hideFooter: true, defaultHeight: true }}
            />
            {Array.from(viewProps.functionalities).length > 0 && (
                <DefaultContainer
                    title='Licenses'
                    content={
                        <div className='grid w-full grid-cols-1 gap-3 lg:grid-cols-3'>
                            {Array.from(viewProps.functionalities).map((functionalityId) => (
                                <PlatformObjectBadge
                                    key={functionalityId}
                                    entityType={EntityType.FUNCTIONALITY}
                                    id={functionalityId}
                                    overwriteDisplayMap={(e: Functionality) => e.application + ' - ' + e.type}
                                    styles={{ icon: 'size-4 font-semibold text-brand-primary' }}
                                    className='outline-brand-primary text-brand-primary flex items-center justify-between gap-2 self-stretch rounded-md px-2.5 py-3.5 font-semibold capitalize outline-1 -outline-offset-1'
                                    disableClick
                                />
                            ))}
                        </div>
                    }
                />
            )}
        </>
    );
};
